/**
 * Sistema de Monitoramento de Erros - Frontend
 * Captura e envia erros para o backend automaticamente
 */

/**
 * 🔧 CORREÇÃO VERSÃO ATUAL: Função para obter URL do backend dinamicamente
 * SEMPRE usa window.location.origin para preview/produção
 * LAZY EVALUATION - só calcula quando chamada, não no import
 */
let _cachedBackendUrl = null;

const getBackendUrl = () => {
  // Retornar cache se já calculado
  if (_cachedBackendUrl) {
    return _cachedBackendUrl;
  }
  
  // SSR/Build time - retornar fallback sem cachear
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  // Localhost = usar .env
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    _cachedBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    return _cachedBackendUrl;
  }
  
  // Preview/Produção = SEMPRE usar origin atual
  _cachedBackendUrl = origin;
  return _cachedBackendUrl;
};

// 🔧 LAZY EVALUATION - não calcular no import, calcular quando usado
const getApiUrl = () => `${getBackendUrl()}/api`;

// Configurações
const ERROR_BATCH_SIZE = 5; // Enviar em lotes
const ERROR_BATCH_INTERVAL = 5000; // 5 segundos
const MAX_ERRORS_PER_SESSION = 100; // Limite por sessão

// Estado
let errorQueue = [];
let errorCount = 0;
let batchTimeout = null;
let isInitialized = false;

/**
 * Categoriza o erro automaticamente
 */
function categorizeError(error, componentName = '', url = '') {
  const errorString = `${error?.message || ''} ${error?.name || ''} ${componentName} ${url}`.toLowerCase();
  
  // 🎵 AUDIO/MEDIA/RADIO
  if (errorString.includes('audio') || errorString.includes('player') || errorString.includes('radio') || 
      errorString.includes('media') || errorString.includes('sound') || errorString.includes('mp3') ||
      errorString.includes('playback') || errorString.includes('decode') || errorString.includes('stream')) {
    return 'audio';
  }
  if (errorString.includes('login') || errorString.includes('senha') || errorString.includes('password')) {
    return 'login';
  }
  if (errorString.includes('auth') || errorString.includes('token') || errorString.includes('unauthorized')) {
    return 'authentication';
  }
  if (errorString.includes('payment') || errorString.includes('pix') || errorString.includes('boleto')) {
    return 'payment';
  }
  if (errorString.includes('certificate') || errorString.includes('certificado')) {
    return 'certificate';
  }
  if (errorString.includes('work') || errorString.includes('obra')) {
    return 'works';
  }
  if (errorString.includes('gallery') || errorString.includes('galeria')) {
    return 'gallery';
  }
  if (errorString.includes('network') || errorString.includes('fetch') || errorString.includes('api')) {
    return 'api';
  }
  if (errorString.includes('chunk') || errorString.includes('loading')) {
    return 'initialization';
  }
  return 'frontend';
}

/**
 * Determina a severidade do erro
 */
function determineSeverity(error, isApiError = false, statusCode = null) {
  if (statusCode && statusCode >= 500) return 'critical';
  if (statusCode === 401 || statusCode === 403) return 'high';
  if (isApiError && statusCode >= 400) return 'medium';
  
  const errorString = (error?.message || '').toLowerCase();
  if (errorString.includes('network') || errorString.includes('fetch failed')) return 'high';
  if (errorString.includes('chunk') || errorString.includes('loading')) return 'medium';
  
  return 'low';
}

/**
 * Obtém informações do usuário atual
 */
function getCurrentUser() {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        user_id: payload.user_id || payload.sub,
        user_email: payload.email
      };
    }
  } catch (e) {
    // Ignore
  }
  return { user_id: null, user_email: null };
}

/**
 * Envia erros para o backend
 */
async function sendErrors(errors) {
  if (!errors.length) return;
  
  try {
    const apiUrl = getBackendUrl() + '/api';
    for (const error of errors) {
      await fetch(`${apiUrl}/errors/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(error),
      });
    }
    console.log(`🐛 ${errors.length} erro(s) enviado(s) para monitoramento`);
  } catch (e) {
    console.warn('⚠️ Falha ao enviar erros para monitoramento:', e);
  }
}

/**
 * Processa a fila de erros
 */
function processErrorQueue() {
  if (errorQueue.length === 0) return;
  
  const errorsToSend = errorQueue.splice(0, ERROR_BATCH_SIZE);
  sendErrors(errorsToSend);
}

/**
 * Adiciona erro à fila
 */
function queueError(errorData) {
  if (errorCount >= MAX_ERRORS_PER_SESSION) {
    console.warn('⚠️ Limite de erros por sessão atingido');
    return;
  }
  
  errorCount++;
  errorQueue.push(errorData);
  
  // Agendar envio em lote
  if (!batchTimeout) {
    batchTimeout = setTimeout(() => {
      processErrorQueue();
      batchTimeout = null;
    }, ERROR_BATCH_INTERVAL);
  }
  
  // Se atingiu o tamanho do lote, enviar imediatamente
  if (errorQueue.length >= ERROR_BATCH_SIZE) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
    processErrorQueue();
  }
}

// Erros do ambiente do navegador do visitante (não são bugs do site)
const IGNORED_ERROR_PATTERNS = [
  /Failed to read the '(local|session)Storage' property/i,
  /ServiceWorker/i,
  /ResizeObserver loop/i,
  /The operation is insecure/i,
];

// 404 esperados: obra/artigo/compositor removido ou link antigo
const EXPECTED_404_PATTERNS = [/\/api\/public\/works\//, /\/api\/articles\//, /\/api\/gallery\/(work|lyrics|audio)\//, /\/api\/compositor\//];

function shouldIgnore(error, options) {
  const msg = `${error?.message || ''} ${error?.name || ''}`;
  if (IGNORED_ERROR_PATTERNS.some((re) => re.test(msg))) return true;
  const status = options.statusCode ?? error?.response?.status;
  const endpoint = options.endpoint || error?.config?.url || '';
  if (status === 404 && EXPECTED_404_PATTERNS.some((re) => re.test(endpoint))) return true;
  return false;
}

/**
 * Registra um erro manualmente com TODOS os detalhes
 */
export function logError(error, options = {}) {
  if (shouldIgnore(error, options)) return;
  const user = getCurrentUser();
  
  // 🔧 VERSÃO ATUAL: Proteção SSR - verificar se window/document existem
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  
  // Coletar informações detalhadas do ambiente (apenas no browser)
  const browserInfo = isBrowser ? {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cookiesEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  } : {
    userAgent: 'SSR',
    language: 'pt-BR',
    platform: 'server',
    cookiesEnabled: false,
    onLine: true,
    screenWidth: 0,
    screenHeight: 0,
    viewportWidth: 0,
    viewportHeight: 0,
  };
  
  // Informações da página (apenas no browser)
  const pageInfo = isBrowser ? {
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    referrer: document.referrer,
    title: document.title,
  } : {
    url: 'SSR',
    pathname: '/',
    search: '',
    hash: '',
    referrer: '',
    title: 'SSR',
  };
  
  // Coletar performance timing se disponível (apenas no browser)
  let performanceInfo = null;
  if (isBrowser) {
    try {
      if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        performanceInfo = {
          pageLoadTime: timing.loadEventEnd - timing.navigationStart,
          domReadyTime: timing.domContentLoadedEventEnd - timing.navigationStart,
        };
      }
    } catch (e) {}
  }
  
  const errorData = {
    error_type: error?.name || 'Error',
    error_message: error?.message || String(error),
    stack_trace: error?.stack || null,
    source: 'frontend',
    category: options.category || categorizeError(error, options.component, isBrowser ? window.location.pathname : '/'),
    severity: options.severity || determineSeverity(error, options.isApiError, options.statusCode),
    page_url: pageInfo.url,
    component: options.component || null,
    user_id: user.user_id,
    user_email: user.user_email,
    user_agent: isBrowser ? navigator.userAgent : 'SSR',
    endpoint: options.endpoint || null,
    http_method: options.method || null,
    http_status: options.statusCode || null,
    request_url: options.requestUrl || null,
    additional_data: {
      ...options.additionalData,
      browser: browserInfo,
      page: pageInfo,
      performance: performanceInfo,
      timestamp_local: new Date().toISOString(),
      timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
    },
  };
  
  queueError(errorData);
  
  // Log detalhado no console também
  console.error(`🐛 [${errorData.severity.toUpperCase()}] ${errorData.category}:`, error);
  console.error(`   📍 Página: ${pageInfo.pathname}`);
  console.error(`   👤 Usuário: ${user.user_email || 'Não logado'}`);
}

/**
 * Wrapper para capturar erros de API (axios/fetch)
 */
export function logApiError(error, config = {}) {
  const statusCode = error?.response?.status || error?.status || null;
  const endpoint = config.url || error?.config?.url || 'unknown';
  const method = config.method || error?.config?.method || 'unknown';
  
  logError(error, {
    isApiError: true,
    statusCode,
    endpoint,
    method,
    requestUrl: endpoint,
    category: categorizeError(error, '', endpoint),
    severity: determineSeverity(error, true, statusCode),
    additionalData: {
      responseData: error?.response?.data,
      requestData: config.data || error?.config?.data,
    }
  });
}

/**
 * Wrapper para capturar erros de componentes React
 */
export function logComponentError(error, errorInfo, componentName = 'Unknown') {
  logError(error, {
    component: componentName,
    additionalData: {
      componentStack: errorInfo?.componentStack,
    }
  });
}

/**
 * Inicializa o monitoramento global de erros
 */
export function initErrorMonitoring() {
  if (isInitialized) return;
  
  // 🔧 VERSÃO ATUAL: Proteção SSR - não inicializar no servidor
  if (typeof window === 'undefined') {
    console.log('⏭️ [errorMonitoring] Pulando inicialização (SSR)');
    return;
  }
  
  isInitialized = true;
  
  // Capturar erros globais não tratados
  window.onerror = (message, source, lineno, colno, error) => {
    logError(error || new Error(message), {
      additionalData: {
        source,
        lineno,
        colno,
      }
    });
    return false; // Não suprimir o erro
  };
  
  // Capturar rejeições de Promise não tratadas
  window.onunhandledrejection = (event) => {
    const error = event.reason;
    logError(error instanceof Error ? error : new Error(String(error)), {
      additionalData: {
        type: 'unhandledrejection',
      }
    });
  };
  
  // Enviar erros pendentes ao fechar a página
  window.addEventListener('beforeunload', () => {
    if (errorQueue.length > 0) {
      // Usar sendBeacon para garantir envio
      const data = JSON.stringify(errorQueue);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${getBackendUrl()}/api/errors/batch`, data);
      }
    }
  });
  
  console.log('✅ Sistema de monitoramento de erros inicializado');
}

/**
 * Error Boundary Hook para componentes funcionais
 */
export function useErrorHandler() {
  return (error, componentName = 'Component') => {
    logError(error, { component: componentName });
  };
}

/**
 * 🎵 Log de erro de áudio/mídia
 * Usar para erros do player de áudio, rádio, etc.
 */
export function logAudioError(error, audioDetails = {}) {
  const { trackId, trackTitle, audioUrl, playbackTime, errorCode } = audioDetails;
  
  // Criar erro se não for instância de Error
  const errorObj = error instanceof Error ? error : new Error(error?.message || 'Erro de áudio desconhecido');
  
  // Determinar código de erro de mídia
  let mediaErrorCode = errorCode;
  if (error?.target?.error) {
    // MediaError codes: 1=ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED
    mediaErrorCode = error.target.error.code;
  }
  
  const mediaErrorMessages = {
    1: 'MEDIA_ERR_ABORTED - Reprodução abortada',
    2: 'MEDIA_ERR_NETWORK - Erro de rede ao carregar áudio',
    3: 'MEDIA_ERR_DECODE - Erro ao decodificar áudio',
    4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - Formato de áudio não suportado'
  };
  
  logError(errorObj, {
    component: 'RadioPlayer',
    category: 'audio',
    severity: mediaErrorCode === 2 || mediaErrorCode === 3 ? 'high' : 'medium',
    additionalData: {
      trackId,
      trackTitle,
      audioUrl: audioUrl ? audioUrl.substring(0, 100) + '...' : null, // Truncar URL
      playbackTime,
      mediaErrorCode,
      mediaErrorMessage: mediaErrorMessages[mediaErrorCode] || 'Erro desconhecido',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'SSR',
      timestamp: new Date().toISOString()
    }
  });
  
  console.error('🔊 Erro de áudio registrado:', {
    track: trackTitle,
    errorCode: mediaErrorCode,
    message: mediaErrorMessages[mediaErrorCode]
  });
}

export default {
  logError,
  logApiError,
  logComponentError,
  logAudioError,
  initErrorMonitoring,
  useErrorHandler,
};
